import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Pause, Play, X } from 'lucide-react';
import {
  formatBytes,
  formatDuration,
  isAudioContentType,
  isImageContentType,
  isVideoContentType,
} from '../utils/boardChatAttachmentUtils';

const VOICE_BAR_COUNT = 32;
const VOICE_SPEEDS = [1, 1.25, 1.5];

function formatVoiceSpeed(rate) {
  if (rate === 1) return '1x';
  return `${rate}x`;
}

/** Picos normalizados (0.2–1) a partir do sinal real do áudio. */
async function extractWaveformPeaks(url, barCount = VOICE_BAR_COUNT) {
  if (!url || typeof AudioContext === 'undefined' && typeof webkitAudioContext === 'undefined') {
    return null;
  }
  const res = await fetch(url);
  if (!res.ok) throw new Error('fetch audio');
  const arrayBuffer = await res.arrayBuffer();
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  try {
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
    const channel = audioBuffer.getChannelData(0);
    if (!channel?.length) return null;
    const block = Math.max(1, Math.floor(channel.length / barCount));
    const peaks = new Array(barCount).fill(0);
    for (let i = 0; i < barCount; i += 1) {
      const start = i * block;
      const end = Math.min(channel.length, start + block);
      let sum = 0;
      let peak = 0;
      let n = 0;
      for (let j = start; j < end; j += 1) {
        const v = Math.abs(channel[j]);
        if (v > peak) peak = v;
        sum += v;
        n += 1;
      }
      // mistura peak + RMS para amplitude mais “falante”
      const rms = n ? sum / n : 0;
      peaks[i] = peak * 0.65 + rms * 0.35;
    }
    const max = Math.max(...peaks, 0.0001);
    return peaks.map((p) => 0.18 + (p / max) * 0.82);
  } finally {
    try {
      await ctx.close();
    } catch {
      /* ignore */
    }
  }
}

function fallbackPeaks(barCount = VOICE_BAR_COUNT) {
  return Array.from({ length: barCount }, (_, i) => 0.28 + ((i * 17) % 50) / 100);
}

export function VoiceDraftPlayer({ src, durationHint }) {
  const audioRef = useRef(null);
  const waveRef = useRef(null);
  const fillRef = useRef(null);
  const fillInnerRef = useRef(null);
  const timeRef = useRef(null);
  const rafRef = useRef(0);
  const playingRef = useRef(false);
  const durationRef = useRef(Number(durationHint) || 0);
  const playOriginMsRef = useRef(0);
  const playOriginTimeRef = useRef(0);
  const lastLabelSecRef = useRef(-1);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(Number(durationHint) || 0);
  const [peaks, setPeaks] = useState(() => fallbackPeaks());
  const [speed, setSpeed] = useState(1);

  const resolveDuration = () => {
    const el = audioRef.current;
    const d = el?.duration;
    if (Number.isFinite(d) && d > 0) return d;
    if (durationRef.current > 0) return durationRef.current;
    const hint = Number(durationHint) || 0;
    return hint > 0 ? hint : 0;
  };

  const paintProgress = (ratio) => {
    const fill = fillRef.current;
    const inner = fillInnerRef.current;
    const wave = waveRef.current;
    if (!fill || !inner || !wave) return;
    const p = Math.min(1, Math.max(0, ratio));
    fill.style.width = `${p * 100}%`;
    inner.style.width = `${wave.clientWidth}px`;
  };

  const readPlaybackTime = () => {
    const el = audioRef.current;
    if (!el) return 0;
    const mediaT = el.currentTime || 0;
    if (!playingRef.current || !playOriginMsRef.current) return mediaT;
    // Enquanto currentTime ainda não anda (buffer WebM), usa relógio do clique.
    if (mediaT <= playOriginTimeRef.current + 0.04) {
      const rate = el.playbackRate || 1;
      const wall =
        playOriginTimeRef.current +
        ((performance.now() - playOriginMsRef.current) / 1000) * rate;
      return Math.max(mediaT, wall);
    }
    return mediaT;
  };

  const paintFromAudio = () => {
    const el = audioRef.current;
    if (!el) return 0;
    const dur = resolveDuration();
    const t = Math.min(dur || Infinity, readPlaybackTime());
    paintProgress(dur > 0 ? t / dur : 0);
    const labelSec = Math.floor(t);
    if (timeRef.current && labelSec !== lastLabelSecRef.current) {
      lastLabelSecRef.current = labelSec;
      const show = !playingRef.current && t < 0.05 ? dur : t;
      timeRef.current.textContent = formatDuration(show);
    }
    return t;
  };

  const stopPaintLoop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
  };

  const startPaintLoop = () => {
    if (rafRef.current) return;
    const tick = () => {
      if (!playingRef.current) {
        rafRef.current = 0;
        return;
      }
      paintFromAudio();
      rafRef.current = requestAnimationFrame(tick);
    };
    paintFromAudio();
    rafRef.current = requestAnimationFrame(tick);
  };

  const markPlayOrigin = () => {
    const el = audioRef.current;
    playOriginMsRef.current = performance.now();
    playOriginTimeRef.current = el?.currentTime || 0;
  };

  useEffect(() => {
    let cancelled = false;
    setPeaks(fallbackPeaks());
    paintProgress(0);
    if (!src) return undefined;
    extractWaveformPeaks(src, VOICE_BAR_COUNT)
      .then((next) => {
        if (!cancelled && next?.length) setPeaks(next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [src]);

  useEffect(() => {
    durationRef.current = duration;
  }, [duration]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;
    const syncDuration = () => {
      const d = el.duration;
      if (Number.isFinite(d) && d > 0) {
        durationRef.current = d;
        setDuration(d);
      } else if (Number(durationHint) > 0) {
        durationRef.current = Number(durationHint);
        setDuration(Number(durationHint));
      }
    };
    const onEnded = () => {
      playingRef.current = false;
      setPlaying(false);
      stopPaintLoop();
      paintProgress(1);
      if (timeRef.current) {
        timeRef.current.textContent = formatDuration(resolveDuration());
      }
    };
    const onPlaying = () => {
      // Áudio de fato tocando — recalibra origem no clock da mídia.
      markPlayOrigin();
      playingRef.current = true;
      setPlaying(true);
      startPaintLoop();
    };
    const onPause = () => {
      playingRef.current = false;
      setPlaying(false);
      stopPaintLoop();
      paintFromAudio();
    };
    el.addEventListener('loadedmetadata', syncDuration);
    el.addEventListener('durationchange', syncDuration);
    el.addEventListener('ended', onEnded);
    el.addEventListener('playing', onPlaying);
    el.addEventListener('pause', onPause);
    syncDuration();
    paintProgress(0);
    if (timeRef.current) {
      timeRef.current.textContent = formatDuration(resolveDuration());
    }
    return () => {
      stopPaintLoop();
      el.removeEventListener('loadedmetadata', syncDuration);
      el.removeEventListener('durationchange', syncDuration);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('playing', onPlaying);
      el.removeEventListener('pause', onPause);
    };
  }, [src, durationHint]);

  useEffect(() => {
    const wave = waveRef.current;
    const inner = fillInnerRef.current;
    if (wave && inner) inner.style.width = `${wave.clientWidth}px`;
    paintFromAudio();
  }, [peaks]);

  const seekToRatio = (ratio) => {
    const el = audioRef.current;
    const dur = resolveDuration();
    if (!el || dur <= 0) return;
    const next = Math.min(dur, Math.max(0, ratio * dur));
    try {
      el.currentTime = next;
      if (playingRef.current) markPlayOrigin();
      paintProgress(next / dur);
      if (timeRef.current) timeRef.current.textContent = formatDuration(next);
      lastLabelSecRef.current = Math.floor(next);
    } catch {
      /* ignore */
    }
  };

  const seekFromClientX = (clientX) => {
    const wave = waveRef.current;
    if (!wave) return;
    const rect = wave.getBoundingClientRect();
    if (!rect.width) return;
    seekToRatio((clientX - rect.left) / rect.width);
  };

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      // Visual no mesmo instante do clique (não espera setState / Promise).
      el.playbackRate = speed;
      markPlayOrigin();
      playingRef.current = true;
      setPlaying(true);
      paintProgress(Math.max(0.01, (el.currentTime || 0) / (resolveDuration() || 1)));
      startPaintLoop();
      el.play().catch(() => {
        playingRef.current = false;
        setPlaying(false);
        stopPaintLoop();
        paintFromAudio();
      });
    } else {
      el.pause();
      playingRef.current = false;
      setPlaying(false);
      stopPaintLoop();
      paintFromAudio();
    }
  };

  const cycleSpeed = (e) => {
    e.stopPropagation();
    const el = audioRef.current;
    const idx = VOICE_SPEEDS.indexOf(speed);
    const next = VOICE_SPEEDS[(idx + 1) % VOICE_SPEEDS.length];
    setSpeed(next);
    if (el) {
      el.playbackRate = next;
      if (playingRef.current) markPlayOrigin();
    }
  };

  const bars = peaks.map((amp, i) => (
    <i key={i} style={{ height: `${Math.round(amp * 100)}%` }} />
  ));

  return (
    <div className="bch-voice-player">
      <audio ref={audioRef} src={src} preload="auto" />
      <button
        type="button"
        className="bch-voice-player-play"
        aria-label={playing ? 'Pausar' : 'Reproduzir'}
        onClick={toggle}
      >
        {playing ? <Pause size={14} strokeWidth={2.2} /> : <Play size={14} strokeWidth={2.2} />}
      </button>
      <div
        ref={waveRef}
        className="bch-voice-player-wave"
        role="slider"
        tabIndex={0}
        aria-label="Posição do áudio"
        aria-valuemin={0}
        aria-valuemax={Math.round(resolveDuration()) || 0}
        onClick={(e) => seekFromClientX(e.clientX)}
        onKeyDown={(e) => {
          const dur = resolveDuration();
          if (!dur) return;
          const el = audioRef.current;
          const t = el?.currentTime || 0;
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            seekToRatio(Math.min(1, (t + dur * 0.05) / dur));
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            seekToRatio(Math.max(0, (t - dur * 0.05) / dur));
          }
        }}
      >
        <div className="bch-voice-player-wave-base">{bars}</div>
        <div ref={fillRef} className="bch-voice-player-wave-fill">
          <div ref={fillInnerRef} className="bch-voice-player-wave-fill-inner">
            {bars}
          </div>
        </div>
      </div>
      <button
        type="button"
        className={`bch-voice-player-speed${speed !== 1 ? ' is-fast' : ''}`}
        aria-label={`Velocidade ${formatVoiceSpeed(speed)}`}
        title="Alterar velocidade"
        onClick={cycleSpeed}
      >
        {formatVoiceSpeed(speed)}
      </button>
      <span ref={timeRef} className="bch-voice-player-time">
        {formatDuration(duration || durationHint || 0)}
      </span>
    </div>
  );
}

/** Faixa de rascunhos de anexo no composer. */
export function ComposerAttachmentDrafts({ items, onRemove }) {
  if (!items?.length) return null;
  return (
    <div className="bch-attach-drafts" aria-label="Anexos em rascunho">
      {items.map((item) => {
        const done = item.status === 'done';
        const err = item.status === 'error';
        const busy = item.status === 'uploading' || item.status === 'validating';
        const showImage =
          item.previewUrl &&
          (item.kind === 'foto' || isImageContentType(item.contentType, item.fileName));
        const showVideo =
          item.previewUrl &&
          (item.kind === 'video' || isVideoContentType(item.contentType, item.fileName));
        const showAudio =
          item.kind === 'audio' || isAudioContentType(item.contentType, item.fileName);

        if (showAudio) {
          return (
            <div
              key={item.localId}
              className={[
                'bch-attach-draft',
                'bch-attach-draft--voice',
                err ? 'is-error' : '',
                busy ? 'is-busy' : '',
                done ? 'is-done' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {item.previewUrl && !err ? (
                <VoiceDraftPlayer src={item.previewUrl} durationHint={item.durationSec} />
              ) : (
                <div className="bch-attach-draft-meta">
                  <span className="bch-attach-draft-name">Nota de voz</span>
                  <span className="bch-attach-draft-sub">
                    {err
                      ? item.error || 'Falha'
                      : busy
                        ? item.status === 'validating'
                          ? 'Validando…'
                          : 'Enviando…'
                        : formatBytes(item.size || 0)}
                  </span>
                </div>
              )}
              <button
                type="button"
                className="bch-attach-draft-remove"
                aria-label="Remover nota de voz"
                onClick={() => onRemove?.(item.localId)}
              >
                <X size={12} strokeWidth={2.2} />
              </button>
            </div>
          );
        }

        return (
          <div
            key={item.localId}
            className={[
              'bch-attach-draft',
              err ? 'is-error' : '',
              busy ? 'is-busy' : '',
              done ? 'is-done' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div className="bch-attach-draft-thumb">
              {showImage ? (
                <img src={item.previewUrl} alt="" />
              ) : showVideo ? (
                <video src={item.previewUrl} muted playsInline preload="metadata" />
              ) : (
                <span className="bch-attach-draft-fallback">{(item.fileName || '?').slice(0, 1)}</span>
              )}
              {busy ? (
                <span className="bch-attach-draft-progress" aria-hidden>
                  {item.progress != null ? `${item.progress}%` : '…'}
                </span>
              ) : null}
            </div>
            <div className="bch-attach-draft-meta">
              <span className="bch-attach-draft-name" title={item.fileName}>
                {item.fileName}
              </span>
              <span className="bch-attach-draft-sub">
                {err
                  ? item.error || 'Falha'
                  : item.status === 'validating'
                    ? 'Validando…'
                    : item.status === 'uploading'
                      ? 'Enviando…'
                      : formatBytes(item.size || 0)}
              </span>
            </div>
            <button
              type="button"
              className="bch-attach-draft-remove"
              aria-label={`Remover ${item.fileName || 'anexo'}`}
              onClick={() => onRemove?.(item.localId)}
            >
              <X size={12} strokeWidth={2.2} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

export function AttachmentLightbox({ items, index, onClose, onIndexChange }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && index > 0) onIndexChange(index - 1);
      if (e.key === 'ArrowRight' && index < items.length - 1) onIndexChange(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, items.length, onClose, onIndexChange]);

  const current = items[index];
  if (!current) return null;
  const multi = items.length > 1;

  return createPortal(
    <div
      className="bch-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Visualizar mídia"
      onClick={onClose}
    >
      <button
        type="button"
        className="bch-lightbox-close"
        aria-label="Fechar"
        onClick={onClose}
      >
        <X size={20} strokeWidth={2} />
      </button>
      {multi && index > 0 ? (
        <button
          type="button"
          className="bch-lightbox-nav bch-lightbox-nav--prev"
          aria-label="Anterior"
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange(index - 1);
          }}
        >
          <ChevronLeft size={28} strokeWidth={1.8} />
        </button>
      ) : null}
      {multi && index < items.length - 1 ? (
        <button
          type="button"
          className="bch-lightbox-nav bch-lightbox-nav--next"
          aria-label="Próxima"
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange(index + 1);
          }}
        >
          <ChevronRight size={28} strokeWidth={1.8} />
        </button>
      ) : null}
      <div
        className="bch-lightbox-stage"
        onClick={(e) => e.stopPropagation()}
      >
        {isVideoContentType(current.contentType, current.filename) ? (
          <video src={current.url} controls autoPlay playsInline />
        ) : (
          <img src={current.url} alt="" />
        )}
        {multi ? (
          <span className="bch-lightbox-count">
            {index + 1} / {items.length}
          </span>
        ) : null}
      </div>
    </div>,
    document.body
  );
}

function mosaicClass(count) {
  if (count <= 1) return 'bch-attach-mosaic--1';
  if (count === 2) return 'bch-attach-mosaic--2';
  if (count === 3) return 'bch-attach-mosaic--3';
  return 'bch-attach-mosaic--4';
}

/** Bolha de anexo (imagem/vídeo/áudio/arquivo) na thread. */
export function MessageAttachmentBubble({
  attachment,
  attachments,
  outgoing,
}) {
  const list = (attachments?.length ? attachments : attachment ? [attachment] : []).filter(
    Boolean
  );
  const [lightboxIndex, setLightboxIndex] = useState(null);

  if (!list.length) return null;

  const allImages = list.every((a) => isImageContentType(a.contentType, a.filename));

  // Misto (ex.: áudio + foto): empilha cada anexo
  if (list.length > 1 && !allImages) {
    return (
      <div className="bch-attach-stack">
        {list.map((item, idx) => (
          <MessageAttachmentBubble
            key={`${item.url || item.filename || 'a'}-${idx}`}
            attachment={item}
            outgoing={outgoing}
          />
        ))}
      </div>
    );
  }

  const single = list[0];
  const isVideo =
    list.length === 1 && isVideoContentType(single.contentType, single.filename);
  const isAudio =
    list.length === 1 && isAudioContentType(single.contentType, single.filename);
  const isFile =
    list.length === 1 &&
    !isImageContentType(single.contentType, single.filename) &&
    !isVideo &&
    !isAudio;

  if (isAudio) {
    return (
      <div
        className={[
          'bch-attach-bubble',
          'bch-attach-bubble--voice',
          outgoing ? 'bch-attach-bubble--out' : 'bch-attach-bubble--in',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <VoiceDraftPlayer src={single.url} />
      </div>
    );
  }

  if (allImages) {
    const count = list.length;
    const visible = count <= 4 ? list : list.slice(0, 4);
    const extra = count > 4 ? count - 4 : 0;
    return (
      <>
        <div
          className={[
            'bch-attach-bubble',
            'bch-attach-bubble--media',
            outgoing ? 'bch-attach-bubble--out' : 'bch-attach-bubble--in',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div className={`bch-attach-mosaic ${mosaicClass(count)}`}>
            {visible.map((item, idx) => {
              const isLastOverlay = extra > 0 && idx === visible.length - 1;
              return (
                <button
                  key={`${item.url}-${idx}`}
                  type="button"
                  className={`bch-attach-tile${count === 1 ? ' bch-attach-tile--solo' : ''}`}
                  onClick={() => setLightboxIndex(idx)}
                  aria-label="Ampliar imagem"
                >
                  <img src={item.url} alt="" />
                  {isLastOverlay ? (
                    <span className="bch-attach-tile-more">+{extra}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
        {lightboxIndex != null ? (
          <AttachmentLightbox
            items={list}
            index={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onIndexChange={setLightboxIndex}
          />
        ) : null}
      </>
    );
  }

  if (isVideo) {
    return (
      <>
        <div
          className={[
            'bch-attach-bubble',
            'bch-attach-bubble--media',
            outgoing ? 'bch-attach-bubble--out' : 'bch-attach-bubble--in',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <button
            type="button"
            className="bch-attach-video"
            onClick={() => setLightboxIndex(0)}
            aria-label="Ampliar vídeo"
          >
            <video src={single.url} muted playsInline preload="metadata" />
            <span className="bch-attach-video-play" aria-hidden>
              ▶
            </span>
          </button>
        </div>
        {lightboxIndex != null ? (
          <AttachmentLightbox
            items={list}
            index={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onIndexChange={setLightboxIndex}
          />
        ) : null}
      </>
    );
  }

  if (isFile) {
    return (
      <div
        className={`bch-attach-bubble${outgoing ? ' bch-attach-bubble--out' : ' bch-attach-bubble--in'}`}
      >
        <a
          className="bch-attach-file"
          href={single.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="bch-attach-file-name">{single.filename}</span>
          <span className="bch-attach-file-size">{formatBytes(single.size)}</span>
        </a>
      </div>
    );
  }

  return null;
}
